<template>
	<Modal
		:title="pageTitle"
		size="xl"
		:frameless="true"
		@close="$emit('close')"
		@confirm="$emit('close')"
	>
		<!-- Error State -->
		<div v-if="error" class="p-5 text-center">
			<Icon
				:icon="['far', 'exclamation-triangle']"
				size="3x"
				class="mb-3 text-danger"
			/>
			<h3 class="mb-2 text-danger">{{ $t('general.error-loading') }}</h3>
			<p class="mb-3 text-muted">{{ errorMessage }}</p>
			<div class="gap-2 d-flex justify-content-center">
				<Button type="primary" :loading="loading" @click="retryLoad">
					<Icon :icon="['far', 'refresh']" class="mr-1" />
					{{ $t('general.retry') }}
				</Button>
				<Button type="tertiary" @click="$emit('close')">
					{{ $t('general.close') }}
				</Button>
			</div>
		</div>

		<!-- Job Offer Content -->
		<div v-else-if="jobOffer" class="p-4">
			<div class="container">
				<div class="gap-4 d-xl-flex">
					<div class="flex-grow-1 position-relative">
						<!-- Job Offer Header -->
						<Heading
							:title="jobOffer.name"
							:prehead="jobOffer.company"
							:preheadUrl="
								jobOffer.organization?.slug
									? `/${jobOffer.organization.slug}`
									: null
							"
						>
							<template #left>
								<div class="d-flex">
									<div
										class="bg-contain border rounded-circle"
										:style="{
											backgroundImage: `url(${
												jobOffer.logo
													? jobOffer.logo.url
													: '/placeholder-company.svg'
											})`,
											backgroundClip: 'padding-box',
											backgroundRepeat: 'no-repeat',
											width: '120px',
											height: '120px'
										}"
									>
										&nbsp;
									</div>
								</div>
							</template>

							<template #actions>
								<div class="mt-3 d-flex ml-md-4 mt-md-0">
									<!-- Show inactive notice if job offer is inactive -->
									<div
										v-if="!jobOffer.active"
										class="mr-2 badge badge-warning"
									>
										<Icon
											:icon="[
												'far',
												'exclamation-triangle'
											]"
											class="mr-1"
										/>
										{{ $t('general.inactive') }}
									</div>

									<!-- Existing applications may still be withdrawn after deactivation. -->
									<ApplicationControls
										v-if="showApplicationControls"
										:loading="applicationLoading"
										class="mb-2 ml-2"
										:application="application"
										:ready-to-submit-application="
											readyToSubmitApplication
										"
										:job-offer="jobOffer"
										@updated:application="
											updateApplication(...$event)
										"
										@submitted:apply="
											apply(
												$applicantProfile.id,
												jobOffer.id
											)
										"
									/>
								</div>
							</template>
						</Heading>

						<!-- Job Description -->
						<div
							class="mt-4 description"
							v-html="jobOffer.description"
							v-if="jobOffer.description"
						/>

						<!-- Personality section -->
						<PersonalityResultsOverview
							v-if="
								$applicantProfile?.personality &&
									jobOffer?.personality
							"
							:job-offer-data="jobOffer"
							:applicant-data="$applicantProfile"
							:match-data="application?.match || match"
							:application-id="
								application ? application.id : null
							"
							wheel-mode="comparison"
						/>

						<!-- Job Details Tables -->
						<div class="mb-3">
							<EducationTable
								:jobOffer="jobOffer"
								:applicantProfile="$applicantProfile"
								:match="match"
							/>
						</div>
						<div class="mb-3">
							<RequirementTable
								:jobOffer="jobOffer"
								:applicantProfile="$applicantProfile"
								:match="match"
							/>
						</div>
						<div class="mb-3">
							<LanguageTable
								:jobOffer="jobOffer"
								:applicantProfile="$applicantProfile"
								:match="match"
							/>
						</div>
					</div>
					<div class="container flex-1 mt-4 job-offer-sidebar">
						<!-- Organization Description -->
						<div
							v-if="jobOffer.organization?.description"
							class="px-3 mb-4 border rounded-lg row job-offer-sidebar-bg"
						>
							<div class="mt-4 text-left col-12">
								<h2>
									{{ $t('general.about') }}
									{{ jobOffer.company }}
								</h2>
							</div>

							<div class="mb-4 col-12">
								{{ jobOffer.organization.description }}

								<div class="flex gap-5 mt-3">
									<NuxtLink
										v-if="jobOffer.organization?.slug"
										:to="`/${jobOffer.organization.slug}`"
									>
										<Icon :icon="['far', 'industry']" />
										{{ $t('organization.profile') }}
									</NuxtLink>
									<span v-if="jobOffer.organization?.url">
										<a :href="jobOffer.organization.url">
											<Icon :icon="['fas', 'link']" />
											{{ $t('general.website') }}
										</a>
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-else class="p-4 text-center">
			<Spinner type="primary">{{ $t('general.loading') }}</Spinner>
		</div>
	</Modal>
</template>

<script>
import EducationTable from '~/components/crud/tables/EducationTable'
import LanguageTable from '~/components/crud/tables/LanguageTable'
import RequirementTable from '~/components/crud/tables/RequirementTable'
import ApplicationControls from '~/components/ui/applications/Controls'
import Heading from '~/components/ui/common/Heading'
import Spinner from '~/components/ui/common/Spinner'
import Modal from '~/components/ui/layout/Modal'
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import { PersonalityResultsOverview } from '~/components/Personality'
import { ANALYTICS_TRIGGERS } from '~/utils/analyticsTriggers'
import { applicationIntent } from '~/utils/applicationIntent'
import {
	canApplicantWithdrawApplication,
	getApplicationStageLabelKey,
	getVisibleApplicationStatus
} from '~/utils/applicantApplicationStage'

export default {
	components: {
		EducationTable,
		LanguageTable,
		RequirementTable,
		ApplicationControls,
		Heading,
		Spinner,
		Modal,
		Button,
		Icon,
		PersonalityResultsOverview
	},
	props: {
		jobOfferId: {
			type: [String, Number],
			required: true
		},
		applicationId: {
			type: [String, Number],
			required: false,
			default: null
		}
	},
	data() {
		return {
			jobOffer: null,
			application: null,
			match: null,
			loading: true,
			applicationLoading: false,
			error: false,
			errorMessage: ''
		}
	},
	computed: {
		showApplicationControls() {
			return Boolean(
				this.$user &&
					this.$applicantProfile &&
					(this.jobOffer?.active ||
						(!this.$isOrgContext &&
							canApplicantWithdrawApplication(
								this.application,
								this.jobOffer
							)))
			)
		},
		pageTitle() {
			if (!this.jobOffer) return this.$t('general.loading')
			return this.jobOffer.name || this.$t('general.job-offer')
		},
		readyToSubmitApplication() {
			if (this.$org || this.application) {
				return false
			}

			return applicationIntent.isReadyToSubmitJobOffer(this.jobOffer?.id)
		},
		applicantProfile() {
			return this.$store.state.applicantProfile.applicantProfile
		}
	},
	async mounted() {
		await this.loadJobOfferData()
	},
	methods: {
		async loadJobOfferData() {
			this.loading = true
			this.error = false

			try {
				await this.$store.dispatch(
					'applicantProfile/needApplicantProfile'
				)

				this.jobOffer = await this.$axios.$get(
					`/job-offer/${this.jobOfferId}`
				)

				if (this.applicationId) {
					this.application = await this.$axios.$get(
						`/application/${this.applicationId}`
					)
				} else {
					this.application = this.$store.state.application.applications.find(
						app =>
							app.job_offer_id == this.jobOfferId &&
							app.applicant_profile_id ==
								this.$applicantProfile?.id
					)
				}

				// Load match data if we have both job offer and applicant profile
				if (
					this.jobOffer &&
					this.$applicantProfile &&
					this.$applicantProfile.active
				) {
					try {
						this.match = await this.$axios.$get(
							`/match?applicant-profile=${this.$applicantProfile.id}&job-offer=${this.jobOfferId}`,
							{ __skip404Toast: true }
						)
					} catch (error) {
						console.warn('Could not load match data:', error)
						// Continue without match data
					}
				}
			} catch (error) {
				console.error('Error loading job offer data:', error)
				this.error = true

				if (error.response?.status === 404) {
					this.errorMessage = this.$t('general.job-offer-not-found')
				} else if (error.response?.status === 403) {
					this.errorMessage = this.$t('general.access-denied')
				} else if (!navigator.onLine) {
					this.errorMessage = this.$t('general.network-error')
				} else {
					this.errorMessage = this.$t('general.unexpected-error')
				}
			} finally {
				this.loading = false
			}
		},

		async retryLoad() {
			await this.loadJobOfferData()
		},

		async updateApplication(id, status, sendRejectionEmail, rejectionResult) {
			this.applicationLoading = true

			try {
				const updatedApplication = await this.$store.dispatch(
					'application/update',
					{
						id,
						status,
						...(typeof sendRejectionEmail === 'boolean' ? { sendRejectionEmail } : {}),
						...(rejectionResult ? { rejectionResult } : {}),
						withdrawal: !this.$isOrgContext && status === 'denied'
					}
				)

				// Update local application
				if (
					this.application &&
					this.application.id === id &&
					updatedApplication
				) {
					this.application = updatedApplication
				}
			} finally {
				this.applicationLoading = false
			}
		},

		async apply(applicantProfileId, jobOfferId) {
			this.applicationLoading = true
			try {
				const pathType = this.$analytics.detectPathType({
					jobId: jobOfferId,
					routePath: this.$route?.path
				})
				const hasCompletedAssessment =
					(this.$applicantProfile?.personality?.length || 0) > 0

				this.$analytics.trackApplicationStarted({
					jobId: jobOfferId,
					pathType,
					hasCompletedAssessment,
					userId: this.$store.state.user?.user?.id,
					applicantProfileId,
					trigger: ANALYTICS_TRIGGERS.JOB_OFFER_MODAL_APPLY,
					userType: 'candidate'
				})

				await this.$store.dispatch('application/store', {
					jobOfferId: jobOfferId,
					applicantProfileId: applicantProfileId,
					pathType,
					trigger: ANALYTICS_TRIGGERS.JOB_OFFER_MODAL_APPLY,
					submissionContext: 'applicant'
				})

				if (String(jobOfferId) === String(this.jobOffer?.id || '')) {
					applicationIntent.clearReadyToSubmitJobOfferId()
				}

				// Reload application data
				this.application = this.$store.state.application.applications.find(
					app =>
						app.job_offer_id == jobOfferId &&
						app.applicant_profile_id == applicantProfileId
				)
			} finally {
				this.applicationLoading = false
			}
		},

		openApplicationInNewTab(applicationId) {
			// Open application details in new tab if needed
			window.open(`/application/${applicationId}`, '_blank')
		},

		getStatusBadgeClass(status) {
			status = getVisibleApplicationStatus(status)
			const statusClasses = {
				pending: 'badge-warning',
				invitation: 'badge-primary',
				accepted: 'badge-success',
				interview: 'badge-info',
				hired: 'badge-success',
				denied: 'badge-danger'
			}
			return statusClasses[status] || 'badge-secondary'
		},

		getStatusIcon(status) {
			status = getVisibleApplicationStatus(status)
			const statusIcons = {
				pending: ['far', 'inbox'],
				invitation: ['far', 'paper-plane'],
				accepted: ['far', 'check'],
				interview: ['far', 'comments'],
				hired: ['far', 'handshake'],
				denied: ['far', 'times']
			}
			return statusIcons[status] || ['far', 'question']
		},

		getStatusLabel(status) {
			return this.$t(
				getApplicationStageLabelKey(
					status,
					false,
					this.application?.decline_source
				)
			)
		}
	}
}
</script>
